import { isHexColor, isNamedColor, normalizeHex } from './colors';
import { CLICK_EVENT_FIELDS, isClickAction, isRawRun } from './compile';
import type { ClickEvent, HoverRun, ParseResult, RawRun, Run, TextRun } from './types';

/**
 * Reading an existing tellraw back into the composer.
 *
 * Staff paste commands from old configs, plugin docs and web generators, in
 * every dialect from 1.7 to today. Whatever the run model can hold becomes
 * ordinary runs with inheritance already resolved, so the editor and the
 * assistant see exactly the style each piece of text is shown in. Whatever it
 * cannot hold (translations, selectors, scores, keybinds, NBT, fonts,
 * insertions, shadow colours, events other than the four clicks and a text
 * hover) is kept as a RawRun: the original subtree, untouched, with the style
 * it inherited written onto its root so it still looks the same once it is a
 * sibling under the compiler's unstyled root. Importing and re-sending must
 * never quietly change what players see.
 *
 * Errors are one plain sentence with a 1-based line and column, because the
 * person reading them is looking at a paste box, not a stack trace. The
 * position comes from the engine's own message when it gives one; V8 leaves
 * it out for some errors (an unexpected character, a trailing comma in a
 * list), so a small scanner finds it then, and also supplies the wording so
 * every browser says the same thing.
 */

const GENERIC_ERROR = 'Paste a tellraw command or its JSON text component.';
const SNBT_ERROR = 'This looks like SNBT from 1.21.5 or newer. Paste the JSON form instead.';
const MAX_DEPTH = 256;

const STYLE_FLAGS = ['bold', 'italic', 'underlined', 'strikethrough', 'obfuscated'] as const;
type StyleFlag = (typeof STYLE_FLAGS)[number];

/** Keys a child inherits from its parent, as written, for the root of a RawRun. */
const INHERITED_KEYS = [
  'color',
  ...STYLE_FLAGS,
  'font',
  'insertion',
  'shadow_color',
  'clickEvent',
  'click_event',
  'hoverEvent',
  'hover_event',
] as const;

/** Style keys the run model has no place for. */
const UNSUPPORTED_STYLE_KEYS = ['font', 'insertion', 'shadow_color'] as const;

/** Content keys in the order the game checks them, and the `type` names that select them. */
const CONTENT_KEYS = ['text', 'translate', 'score', 'selector', 'keybind', 'nbt', 'object'] as const;
const CONTENT_TYPES = new Map<string, string>([
  ['text', 'text'],
  ['translatable', 'translate'],
  ['score', 'score'],
  ['selector', 'selector'],
  ['keybind', 'keybind'],
  ['nbt', 'nbt'],
  ['object', 'object'],
]);

type Json = Record<string, unknown>;

const isRecord = (value: unknown): value is Json =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isPrimitiveText = (value: unknown): value is string | number | boolean =>
  typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';

class TooDeep extends Error {}

type Failure = Extract<ParseResult, { ok: false }>;

/* ── Positions ───────────────────────────────────────────────────────────── */

function lineColumn(input: string, offset: number): { line: number; column: number } {
  let line = 1;
  let column = 1;
  for (let i = 0; i < offset && i < input.length; i += 1) {
    if (input[i] === '\n') {
      line += 1;
      column = 1;
    } else {
      column += 1;
    }
  }
  return { line, column };
}

function offsetOf(text: string, line: number, column: number): number {
  let offset = 0;
  for (let current = 1; current < line && offset < text.length; offset += 1) {
    if (text[offset] === '\n') current += 1;
  }
  return Math.min(offset + column - 1, text.length);
}

function failure(error: string, input?: string, offset?: number): Failure {
  if (input === undefined || offset === undefined) return { ok: false, error };
  return { ok: false, error, ...lineColumn(input, offset) };
}

/* ── Finding the JSON in a command ───────────────────────────────────────── */

const skipSpace = (input: string, from: number) => {
  let i = from;
  while (i < input.length && /\s/.test(input[i])) i += 1;
  return i;
};

/** Index of the bracket closing the one at `open`, skipping quoted strings; -1 when unclosed. */
function closingBracket(input: string, open: number): number {
  let depth = 0;
  let quoteChar: string | null = null;
  for (let i = open; i < input.length; i += 1) {
    const char = input[i];
    if (quoteChar) {
      if (char === '\\') i += 1;
      else if (char === quoteChar) quoteChar = null;
    } else if (char === '"' || char === "'") {
      quoteChar = char;
    } else if (char === '[' || char === '{') {
      depth += 1;
    } else if (char === ']' || char === '}') {
      depth -= 1;
      if (depth === 0) return i;
    }
  }
  return -1;
}

type Located = { ok: true; selector: string | null; start: number } | Failure;

function locateJson(input: string): Located {
  let i = skipSpace(input, 0);
  if (i >= input.length) return failure(GENERIC_ERROR);
  const slashed = input[i] === '/';
  if (slashed) i += 1;
  const command = /^(?:minecraft:)?tellraw(?=\s|$)/i.exec(input.slice(i));
  if (!command) {
    if (!slashed && '{["'.includes(input[i])) return { ok: true, selector: null, start: i };
    return failure(GENERIC_ERROR);
  }
  i = skipSpace(input, i + command[0].length);
  const targetStart = i;
  if (/^@[aeprsn](?!\w)/.test(input.slice(i))) {
    i += 2;
    if (input[i] === '[') {
      const close = closingBracket(input, i);
      if (close < 0) return failure('The target selector is missing its closing bracket.', input, i);
      i = close + 1;
    }
  } else {
    const name = /^[0-9A-Za-z_.+-]+/.exec(input.slice(i));
    if (!name) return failure('Add a target such as @a after tellraw.', input, i);
    i += name[0].length;
  }
  const selector = input.slice(targetStart, i);
  i = skipSpace(input, i);
  if (i >= input.length) return failure('Add the JSON text component after the target.', input, i);
  return { ok: true, selector, start: i };
}

/* ── JSON errors ─────────────────────────────────────────────────────────── */

/** The error offset the engine reports, if it reports one. */
function engineOffset(message: string, text: string): number | null {
  const position = /at position (\d+)/.exec(message);
  if (position) return Math.min(Number(position[1]), text.length);
  const lineColumnMatch = /line (\d+) column (\d+)/.exec(message);
  if (lineColumnMatch) return offsetOf(text, Number(lineColumnMatch[1]), Number(lineColumnMatch[2]));
  if (/end of (JSON input|data)/i.test(message)) return text.length;
  return null;
}

class Problem {
  constructor(readonly offset: number, readonly message: string) {}
}

/**
 * The first syntax error in `text`, worded for a person. It reports the same
 * offset V8 does, so the wording and the engine's position always agree.
 */
function findJsonProblem(text: string): Problem | null {
  const end = text.length;
  let i = 0;
  const space = () => {
    while (i < end && ' \t\n\r'.includes(text[i])) i += 1;
  };
  const endOfInput = () => new Problem(end, 'The JSON ends before it is complete.');
  const unexpected = (wanted: string): Problem => {
    if (i >= end) return endOfInput();
    const char = text[i];
    if (char === "'") return new Problem(i, 'Use double quotes instead of single quotes.');
    return new Problem(i, wanted);
  };

  function readValue(): void {
    space();
    if (i >= end) throw endOfInput();
    const char = text[i];
    if (char === '{') return readObject();
    if (char === '[') return readArray();
    if (char === '"') return readString();
    if (char === '-' || (char >= '0' && char <= '9')) return readNumber();
    const literal = ['true', 'false', 'null'].find(word => word[0] === char);
    if (literal) {
      // Matched a letter at a time, as V8 does. V8 gives a position only when
      // the stray character starts a number or a string; otherwise point at the word.
      const start = i;
      for (const expected of literal) {
        if (i >= end) throw endOfInput();
        if (text[i] !== expected) {
          throw new Problem(/[-0-9"]/.test(text[i]) ? i : start, 'Text values must be in double quotes.');
        }
        i += 1;
      }
      return;
    }
    if (/[A-Za-z_]/.test(char)) throw new Problem(i, 'Text values must be in double quotes.');
    throw unexpected(`Expected a value but found “${char}”.`);
  }

  function readKey(afterComma: boolean): void {
    space();
    if (i >= end) throw endOfInput();
    if (text[i] === '"') return readString();
    if (afterComma && text[i] === '}') throw new Problem(i, 'Remove the comma before the closing brace.');
    if (/[A-Za-z_]/.test(text[i])) throw new Problem(i, 'Keys must be in double quotes.');
    throw unexpected(afterComma ? 'Expected a key in double quotes.' : 'Expected a key in double quotes or a closing brace.');
  }

  function readObject(): void {
    i += 1;
    space();
    if (text[i] === '}') {
      i += 1;
      return;
    }
    let afterComma = false;
    for (;;) {
      readKey(afterComma);
      space();
      if (text[i] !== ':') throw unexpected('Expected a colon after the key.');
      i += 1;
      readValue();
      space();
      if (text[i] === ',') {
        i += 1;
        afterComma = true;
        continue;
      }
      if (text[i] === '}') {
        i += 1;
        return;
      }
      throw unexpected('Expected a comma or a closing brace.');
    }
  }

  function readArray(): void {
    i += 1;
    space();
    if (text[i] === ']') {
      i += 1;
      return;
    }
    for (;;) {
      space();
      if (text[i] === ']') throw new Problem(i, 'Remove the comma before the closing bracket.');
      readValue();
      space();
      if (text[i] === ',') {
        i += 1;
        continue;
      }
      if (text[i] === ']') {
        i += 1;
        return;
      }
      throw unexpected('Expected a comma or a closing bracket.');
    }
  }

  function readString(): void {
    i += 1;
    while (i < end) {
      const char = text[i];
      if (char === '"') {
        i += 1;
        return;
      }
      if (char < ' ') throw new Problem(i, 'Write line breaks and tabs inside a string as \\n and \\t.');
      if (char === '\\') {
        i += 1;
        if (i >= end) break;
        const escape = text[i];
        if (escape === 'u') {
          for (let digit = 0; digit < 4; digit += 1) {
            i += 1;
            if (i >= end) break;
            if (!/[0-9A-Fa-f]/.test(text[i])) throw new Problem(i, 'A \\u escape needs four hex digits.');
          }
        } else if (!'"\\/bfnrt'.includes(escape)) {
          throw new Problem(i, 'A string has an unknown escape sequence.');
        }
      }
      i += 1;
    }
    throw new Problem(end, 'A string is missing its closing quote.');
  }

  function readNumber(): void {
    const bad = () => new Problem(i, 'A number is written incorrectly.');
    const digits = () => {
      if (!(text[i] >= '0' && text[i] <= '9')) throw bad();
      while (text[i] >= '0' && text[i] <= '9') i += 1;
    };
    if (text[i] === '-') i += 1;
    if (text[i] === '0') {
      i += 1;
      if (text[i] >= '0' && text[i] <= '9') throw bad();
    } else {
      digits();
    }
    if (text[i] === '.') {
      i += 1;
      digits();
    }
    if (text[i] === 'e' || text[i] === 'E') {
      i += 1;
      if (text[i] === '+' || text[i] === '-') i += 1;
      digits();
    }
  }

  try {
    readValue();
    space();
    if (i < end) return new Problem(i, 'There is more text after the JSON.');
    return null;
  } catch (problem) {
    return problem instanceof Problem ? problem : null;
  }
}

/** The start of the first unquoted key (or typed number like `1b`) outside strings, or -1. */
function findSnbt(text: string): number {
  let masked = '';
  let inString = false;
  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    if (inString) {
      if (char === '\\') {
        masked += '  ';
        i += 1;
        continue;
      }
      if (char === '"') inString = false;
      masked += char === '"' ? '"' : ' ';
    } else {
      if (char === '"') inString = true;
      masked += char;
    }
  }
  const key = /[{,]\s*([A-Za-z_][\w.+-]*)\s*:/.exec(masked);
  const typed = /:\s*(-?\d+(?:\.\d+)?[bBsSlLfFdD])\s*[,}\]]/.exec(masked);
  const found = [key, typed]
    .filter((match): match is RegExpExecArray => match !== null)
    .map(match => match.index + match[0].indexOf(match[1]));
  return found.length > 0 ? Math.min(...found) : -1;
}

function jsonFailure(input: string, start: number, error: unknown): Failure {
  const text = input.slice(start);
  const snbt = findSnbt(text);
  if (snbt >= 0) return failure(SNBT_ERROR, input, start + snbt);
  const problem = findJsonProblem(text);
  const offset = engineOffset(error instanceof Error ? error.message : String(error), text) ?? problem?.offset;
  const message = problem?.message ?? 'This is not valid JSON.';
  return offset === undefined ? failure(message) : failure(message, input, start + offset);
}

/* ── Converting to runs ──────────────────────────────────────────────────── */

interface Scope {
  color: string | null;
  flags: Record<StyleFlag, boolean>;
  click: ClickEvent | null;
  hover: HoverRun[] | null;
  /** The inherited keys as they were written, for the root of a RawRun. */
  source: Json;
  /** Something inherited cannot be expressed as a run, so everything below is kept raw. */
  raw: boolean;
}

const ROOT_SCOPE: Scope = {
  color: null,
  flags: { bold: false, italic: false, underlined: false, strikethrough: false, obfuscated: false },
  click: null,
  hover: null,
  source: {},
  raw: false,
};

/** Deep copy of JSON-shaped data; `Object.fromEntries` keeps a `__proto__` key as plain data. */
function copyJson(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(copyJson);
  if (isRecord(value)) return Object.fromEntries(Object.entries(value).map(([key, inner]) => [key, copyJson(inner)]));
  return value;
}

function readColor(value: unknown): string | undefined {
  if (isNamedColor(value)) return value;
  if (typeof value === 'string' && isHexColor(value)) return normalizeHex(value);
  return undefined;
}

function readFlag(value: unknown): boolean | undefined {
  if (value === true || value === 1 || value === 'true') return true;
  if (value === false || value === 0 || value === 'false') return false;
  return undefined;
}

function contentKey(node: Json): string | null {
  if (typeof node.type === 'string') return CONTENT_TYPES.get(node.type) ?? null;
  return CONTENT_KEYS.find(key => node[key] !== undefined) ?? null;
}

const hasUnsupportedStyle = (node: Json) => UNSUPPORTED_STYLE_KEYS.some(key => node[key] != null);

function styleOf(node: Json, color: string | null, flags: Record<StyleFlag, boolean>) {
  const own = { ...flags };
  for (const flag of STYLE_FLAGS) own[flag] = readFlag(node[flag]) ?? flags[flag];
  return { color: readColor(node.color) ?? color, flags: own };
}

/** A click from one spelling, or null when it is set to something the run model cannot hold. */
function readClickSpelling(event: unknown, current: boolean): ClickEvent | null {
  if (!isRecord(event) || !isClickAction(event.action)) return null;
  const value = event[current ? CLICK_EVENT_FIELDS[event.action] : 'value'];
  return typeof value === 'string' ? { action: event.action, value } : null;
}

/** The node's own click: undefined when it sets none, null when the run model cannot hold it. */
function readClick(node: Json): ClickEvent | null | undefined {
  const spellings = [
    node.clickEvent == null ? undefined : readClickSpelling(node.clickEvent, false),
    node.click_event == null ? undefined : readClickSpelling(node.click_event, true),
  ].filter(click => click !== undefined);
  if (spellings.length === 0) return undefined;
  if (spellings.includes(null)) return null;
  const [first, second] = spellings as ClickEvent[];
  if (second && (first.action !== second.action || first.value !== second.value)) return null;
  return first;
}

/** A tooltip's text as hover runs, or null when it holds anything but plain styled text. */
function hoverRuns(component: unknown, depth: number): HoverRun[] | null {
  const runs: HoverRun[] = [];
  const walkHover = (node: unknown, color: string | null, flags: Record<StyleFlag, boolean>, level: number): ReturnType<typeof styleOf> | null => {
    if (level > MAX_DEPTH) throw new TooDeep();
    const inherited = { color, flags };
    if (isPrimitiveText(node)) {
      const text = String(node);
      if (text) runs.push({ text, color, gradient: null, ...flags });
      return inherited;
    }
    if (Array.isArray(node)) {
      if (node.length === 0) return inherited;
      const parent = walkHover(node[0], color, flags, level + 1);
      if (!parent) return null;
      for (const sibling of node.slice(1)) {
        if (!walkHover(sibling, parent.color, parent.flags, level + 1)) return null;
      }
      return parent;
    }
    if (!isRecord(node) || contentKey(node) !== 'text' || !isPrimitiveText(node.text) || hasUnsupportedStyle(node)) {
      return null;
    }
    // Clicks and hovers inside a tooltip do nothing in game, so they are dropped.
    const own = styleOf(node, color, flags);
    const text = String(node.text);
    if (text) runs.push({ text, color: own.color, gradient: null, ...own.flags });
    if (Array.isArray(node.extra)) {
      for (const child of node.extra) {
        if (!walkHover(child, own.color, own.flags, level + 1)) return null;
      }
    }
    return own;
  };
  if (!walkHover(component, null, ROOT_SCOPE.flags, depth + 1)) return null;
  return mergeAdjacent(runs, sameStyle);
}

function readHoverSpelling(event: unknown, current: boolean, depth: number): HoverRun[] | null {
  if (!isRecord(event) || event.action !== 'show_text') return null;
  const content = current
    ? event.value !== undefined ? event.value : event.text
    : event.contents !== undefined ? event.contents : event.value;
  return content === undefined ? null : hoverRuns(content, depth);
}

/** The node's own hover: undefined when it sets none, null when the run model cannot hold it. */
function readHover(node: Json, depth: number): HoverRun[] | null | undefined {
  const spellings = [
    node.hoverEvent == null ? undefined : readHoverSpelling(node.hoverEvent, false, depth),
    node.hover_event == null ? undefined : readHoverSpelling(node.hover_event, true, depth),
  ].filter(hover => hover !== undefined);
  if (spellings.length === 0) return undefined;
  if (spellings.includes(null)) return null;
  const [first, second] = spellings as HoverRun[][];
  if (second && !sameHover(first, second)) return null;
  return first;
}

/** The inherited keys as written, without event spellings the node replaces with its own. */
function inheritedSource(source: Json, node: Json): Json {
  const inherited = { ...source };
  if (node.clickEvent !== undefined || node.click_event !== undefined) {
    delete inherited.clickEvent;
    delete inherited.click_event;
  }
  if (node.hoverEvent !== undefined || node.hover_event !== undefined) {
    delete inherited.hoverEvent;
    delete inherited.hover_event;
  }
  return inherited;
}

function mergedSource(source: Json, node: Json): Json {
  const merged = inheritedSource(source, node);
  for (const key of INHERITED_KEYS) {
    if (node[key] !== undefined) merged[key] = node[key];
  }
  return merged;
}

function textRun(text: string, scope: Scope): TextRun {
  return {
    text,
    color: scope.color,
    gradient: null,
    ...scope.flags,
    click: scope.click ? { ...scope.click } : null,
    hover: scope.hover && scope.hover.length > 0 ? { runs: scope.hover.map(run => ({ ...run })) } : null,
  };
}

function rawRun(raw: Json): RawRun {
  return { raw: copyJson(raw) as Json };
}

/** Emits the runs for `node` and returns the scope it establishes, which array siblings inherit. */
function walk(node: unknown, scope: Scope, out: Run[], depth: number): Scope {
  if (depth > MAX_DEPTH) throw new TooDeep();
  if (isPrimitiveText(node)) {
    const text = String(node);
    if (text) out.push(scope.raw ? rawRun({ text, ...scope.source }) : textRun(text, scope));
    return scope;
  }
  if (Array.isArray(node)) {
    if (node.length === 0) return scope;
    const parent = walk(node[0], scope, out, depth + 1);
    for (const sibling of node.slice(1)) walk(sibling, parent, out, depth + 1);
    return parent;
  }
  if (!isRecord(node)) return scope;

  const click = readClick(node);
  const hover = readHover(node, depth);
  const style = styleOf(node, scope.color, scope.flags);
  const next: Scope = {
    color: style.color,
    flags: style.flags,
    click: click === undefined ? scope.click : click,
    hover: hover === undefined ? scope.hover : hover,
    source: mergedSource(scope.source, node),
    raw: scope.raw || click === null || hover === null || hasUnsupportedStyle(node),
  };
  if (next.raw || contentKey(node) !== 'text' || !isPrimitiveText(node.text)) {
    out.push(rawRun({ ...inheritedSource(scope.source, node), ...node }));
    return next;
  }
  const text = String(node.text);
  if (text) out.push(textRun(text, next));
  if (Array.isArray(node.extra)) {
    for (const child of node.extra) walk(child, next, out, depth + 1);
  }
  return next;
}

function sameStyle(a: HoverRun, b: HoverRun): boolean {
  return a.color === b.color && a.gradient === null && b.gradient === null && STYLE_FLAGS.every(flag => a[flag] === b[flag]);
}

function sameHover(a: HoverRun[], b: HoverRun[]): boolean {
  return a.length === b.length && a.every((run, index) => run.text === b[index].text && sameStyle(run, b[index]));
}

function sameRun(a: TextRun, b: TextRun): boolean {
  const sameClick = a.click === null || b.click === null
    ? a.click === b.click
    : a.click.action === b.click.action && a.click.value === b.click.value;
  const sameHovers = a.hover === null || b.hover === null ? a.hover === b.hover : sameHover(a.hover.runs, b.hover.runs);
  return sameStyle(a, b) && sameClick && sameHovers;
}

function mergeAdjacent<T extends { text: string }>(runs: T[], same: (a: T, b: T) => boolean): T[] {
  const merged: T[] = [];
  for (const run of runs) {
    const last = merged[merged.length - 1];
    if (last && same(last, run)) last.text += run.text;
    else merged.push(run);
  }
  return merged;
}

function mergeRuns(runs: Run[]): Run[] {
  const merged: Run[] = [];
  for (const run of runs) {
    const last = merged[merged.length - 1];
    if (last && !isRawRun(last) && !isRawRun(run) && sameRun(last, run)) last.text += run.text;
    else merged.push(run);
  }
  return merged;
}

/**
 * A pasted tellraw command (`/tellraw @a[…] <json>`) or bare JSON text
 * component, as composer runs. Never throws.
 */
export function parseTellraw(input: string): ParseResult {
  if (typeof input !== 'string') return failure(GENERIC_ERROR);
  const located = locateJson(input);
  if (!located.ok) return located;
  let component: unknown;
  try {
    component = JSON.parse(input.slice(located.start));
  } catch (error) {
    return jsonFailure(input, located.start, error);
  }
  if (!isRecord(component) && !Array.isArray(component) && typeof component !== 'string') {
    return failure(GENERIC_ERROR);
  }
  const runs: Run[] = [];
  try {
    walk(component, ROOT_SCOPE, runs, 0);
  } catch (error) {
    if (error instanceof TooDeep || error instanceof RangeError) {
      return failure('This text component is nested too deeply to import.');
    }
    throw error;
  }
  const merged = mergeRuns(runs);
  return {
    ok: true,
    selector: located.selector,
    component,
    message: { runs: merged },
    editable: !merged.some(isRawRun),
  };
}
